import { useEffect, useRef, useState, useCallback } from "react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { apiClient, formatApiError } from "@/lib/api"
import { useToast } from "@stayframe/ui"
import type { TvSlideConfig } from "@/public/guidebook/types"

// ─── Types ──────────────────────────────────────────────────────────

type MessageType = "welcome" | "during_stay" | "last_evening" | "checkout" | "between_bookings" | "night_trivia"
type Lang = "sv" | "en" | "de"

interface FallbackMessage {
  id: string
  property_id: string
  message_type: string
  lang: string
  headline: string
  message: string
  source: string
  updated_at: string
}

interface NightModeConfig {
  enabled: boolean
  start_hour: number
  start_minute: number
  end_hour: number
  end_minute: number
  area_name: string
  area_type: string
}

interface TriviaFact {
  lang: string
  headline: string
  fact: string
}

interface TriviaPoolStatus {
  area_name: string
  pools: { sv: number; en: number; de: number }
  total: number
  facts: TriviaFact[]
}

// ─── Constants ──────────────────────────────────────────────────────

const MESSAGE_TYPES: { key: MessageType; label: string; description: string }[] = [
  { key: "welcome", label: "Welcome", description: "Shown when guests check in" },
  { key: "during_stay", label: "During Stay", description: "Daily messages during the stay" },
  { key: "last_evening", label: "Last Evening", description: "Shown on the final evening" },
  { key: "checkout", label: "Checkout", description: "Shown on checkout day" },
  { key: "between_bookings", label: "Between Bookings", description: "When no guest is checked in" },
  { key: "night_trivia", label: "Night Trivia", description: "Night mode fallback" },
]

const LANGS: { key: Lang; label: string }[] = [
  { key: "en", label: "English" },
  { key: "sv", label: "Svenska" },
  { key: "de", label: "Deutsch" },
]

const DEFAULT_NIGHT_MODE: NightModeConfig = {
  enabled: false,
  start_hour: 22,
  start_minute: 0,
  end_hour: 6,
  end_minute: 0,
  area_name: "",
  area_type: "town",
}

// ─── Page Component ─────────────────────────────────────────────────

export function DisplayBehaviourPage() {
  const { propertyId } = usePropertyContext()

  // Night mode state
  const [nightMode, setNightMode] = useState<NightModeConfig>({ ...DEFAULT_NIGHT_MODE })
  const [nightModeDirty, setNightModeDirty] = useState(false)
  const [nightModeSaving, setNightModeSaving] = useState(false)
  const [nightModeSuccess, setNightModeSuccess] = useState("")
  const nightModeRef = useRef(nightMode)
  const nightModeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fallback messages state
  const [fallbacks, setFallbacks] = useState<FallbackMessage[]>([])
  const activeLang: Lang = "en"
  const [fallbacksLoading, setFallbacksLoading] = useState(true)

  // Editor drawer state
  const [editingType, setEditingType] = useState<MessageType | null>(null)
  const [editorHeadline, setEditorHeadline] = useState("")
  const [editorMessage, setEditorMessage] = useState("")
  const [editorSaving, setEditorSaving] = useState(false)
  const [editorError, setEditorError] = useState("")
  const [populatingType, setPopulatingType] = useState<string | null>(null)

  // Trivia pool state
  const [triviaPool, setTriviaPool] = useState<TriviaPoolStatus>({ area_name: "", pools: { sv: 0, en: 0, de: 0 }, total: 0, facts: [] })
  const [generatingLang, setGeneratingLang] = useState<string | null>(null)

  // Loading
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  // ─── Data Loading ───────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    if (!propertyId) return
    setLoading(true)
    setError("")
    try {
      const [nmRes, fbRes, tpRes] = await Promise.all([
        apiClient.getNightMode(propertyId),
        apiClient.getFallbackMessages(propertyId),
        apiClient.getTriviaPool(propertyId, true),
      ])
      if (nmRes.ok && nmRes.config) {
        const cfg = { ...DEFAULT_NIGHT_MODE, ...nmRes.config }
        setNightMode(cfg)
        nightModeRef.current = cfg
      }
      if (fbRes.ok && fbRes.fallbacks) setFallbacks(fbRes.fallbacks)
      if (tpRes.ok) {
        setTriviaPool({
          area_name: tpRes.area_name || "",
          pools: tpRes.pools || { sv: 0, en: 0, de: 0 },
          total: tpRes.total || 0,
          facts: (tpRes.facts || []) as TriviaFact[],
        })
      }
    } catch (e) {
      setError("Failed to load display behaviour settings.")
    } finally {
      setLoading(false)
      setFallbacksLoading(false)
    }
  }, [propertyId])

  useEffect(() => { loadAll() }, [loadAll])

  // ─── Night Mode Autosave ────────────────────────────────────────

  const updateNightMode = useCallback(
    (updater: (prev: NightModeConfig) => NightModeConfig) => {
      setNightMode((prev) => {
        const next = updater(prev)
        nightModeRef.current = next
        setNightModeDirty(true)
        return next
      })
    },
    [],
  )

  useEffect(() => {
    if (!nightModeDirty || !propertyId) return
    if (nightModeTimer.current) clearTimeout(nightModeTimer.current)
    nightModeTimer.current = setTimeout(async () => {
      setNightModeSaving(true)
      try {
        const res = await apiClient.saveNightMode(nightModeRef.current as unknown as Record<string, unknown>, propertyId)
        if (res.ok) {
          setNightModeSuccess("Saved")
          setTimeout(() => setNightModeSuccess(""), 2000)
        }
      } catch {}
      setNightModeSaving(false)
      setNightModeDirty(false)
    }, 1500)
    return () => {
      if (nightModeTimer.current) clearTimeout(nightModeTimer.current)
    }
  }, [nightModeDirty, nightMode, propertyId])

  // ─── Fallback Helpers ───────────────────────────────────────────

  const getFallbackForType = (type: MessageType): FallbackMessage | undefined =>
    fallbacks.find((f) => f.message_type === type && f.lang === activeLang)

  const getStatusLabel = (fb: FallbackMessage | undefined): { label: string; className: string } => {
    if (!fb) return { label: "Default", className: "status-pill--neutral" }
    if (fb.source === "ai") return { label: "AI Draft", className: "status-pill--healthy" }
    return { label: "Custom", className: "status-pill--warning" }
  }

  const openEditor = (type: MessageType) => {
    const existing = getFallbackForType(type)
    setEditingType(type)
    setEditorHeadline(existing?.headline || "")
    setEditorMessage(existing?.message || "")
    setEditorError("")
  }

  const closeEditor = () => {
    setEditingType(null)
    setEditorHeadline("")
    setEditorMessage("")
    setEditorError("")
  }

  const saveEditor = async () => {
    if (!editingType || !propertyId) return
    setEditorSaving(true)
    setEditorError("")
    try {
      const res = await apiClient.saveFallbackMessage(
        {
          message_type: editingType,
          lang: activeLang,
          headline: editorHeadline.trim(),
          message: editorMessage.trim(),
          source: "manual",
        },
        propertyId,
      )
      if (res.ok) {
        const fbRes = await apiClient.getFallbackMessages(propertyId)
        if (fbRes.ok && fbRes.fallbacks) setFallbacks(fbRes.fallbacks)
        closeEditor()
      } else {
        setEditorError(res.error || "Failed to save.")
      }
    } catch {
      setEditorError("Failed to save fallback message.")
    }
    setEditorSaving(false)
  }

  const resetFallback = async () => {
    if (!editingType || !propertyId) return
    setEditorSaving(true)
    try {
      const res = await apiClient.resetFallbackMessage(editingType, activeLang, propertyId)
      if (res.ok) {
        const fbRes = await apiClient.getFallbackMessages(propertyId)
        if (fbRes.ok && fbRes.fallbacks) setFallbacks(fbRes.fallbacks)
        closeEditor()
      }
    } catch {}
    setEditorSaving(false)
  }

  const populateByAI = async (type: MessageType, inDrawer = false) => {
    if (!propertyId) return
    const key = `${type}-${activeLang}`
    setPopulatingType(key)
    try {
      const res = await apiClient.populateFallbackByAI(type, activeLang, propertyId)
      if (res.ok && res.draft) {
        if (inDrawer) {
          setEditorHeadline(res.draft.headline)
          setEditorMessage(res.draft.message)
        } else {
          // Save AI draft directly
          const saveRes = await apiClient.saveFallbackMessage(
            {
              message_type: type,
              lang: activeLang,
              headline: res.draft.headline,
              message: res.draft.message,
              source: "ai",
            },
            propertyId,
          )
          if (saveRes.ok) {
            const fbRes = await apiClient.getFallbackMessages(propertyId)
            if (fbRes.ok && fbRes.fallbacks) setFallbacks(fbRes.fallbacks)
          }
        }
      }
    } catch {}
    setPopulatingType(null)
  }

  // ─── Trivia Pool ────────────────────────────────────────────────

  const generateTrivia = async (lang: Lang) => {
    if (!propertyId) return
    setGeneratingLang(lang)
    try {
      const res = await apiClient.generateTriviaPool(lang, propertyId)
      if (res.ok) {
        const tpRes = await apiClient.getTriviaPool(propertyId, true)
        if (tpRes.ok) {
          setTriviaPool({
            area_name: tpRes.area_name || "",
            pools: tpRes.pools || { sv: 0, en: 0, de: 0 },
            total: tpRes.total || 0,
            facts: (tpRes.facts || []) as TriviaFact[],
          })
        }
      }
    } catch {}
    setGeneratingLang(null)
  }

  // ─── Render ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="sf-spinner" />
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="page-header mb-6">
        <h1 className="page-header__title">Display Behaviour</h1>
        <p className="page-header__subtitle">
          Control how Stayframe communicates when automation is unavailable or when Night Mode is active.
        </p>
      </div>

      {error && (
        <div className="sf-result-banner sf-result-banner--error">{error}</div>
      )}

      {/* Section 0: TRMNL Integration */}
      <TrmnlIntegrationCard />

      {/* Section 0b: TV Display */}
      <TvDisplayCard />

      {/* Section 1: Night Mode */}
      <NightModeCard
        config={nightMode}
        onChange={updateNightMode}
        saving={nightModeSaving}
        success={nightModeSuccess}
      />

      {/* Section 2: Fallback Messages */}
      <h3 className="hr-section-group mt-8 mb-3">
        Fallback Messages
      </h3>
      <p className="page-header__subtitle mb-4 mt-0 text-xs">
        Used when automatic message generation is unavailable.
      </p>


      {fallbacksLoading ? (
        <div className="text-center p-8">
          <div className="sf-spinner" />
        </div>
      ) : (
        <div className="db-fallback-table-wrap">
          <table className="db-fallback-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Status</th>
                <th>Preview</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {MESSAGE_TYPES.map((mt) => {
                const fb = getFallbackForType(mt.key)
                const status = getStatusLabel(fb)
                const popKey = `${mt.key}-${activeLang}`
                const preview = fb?.headline
                  ? `${fb.headline} — ${fb.message || ""}`
                  : fb?.message || mt.description

                return (
                  <tr
                    key={mt.key}
                    className="db-fallback-row"
                    onClick={() => openEditor(mt.key)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openEditor(mt.key) } }}
                    tabIndex={0}
                    role="button"
                  >
                    <td>
                      <div className="db-fallback-row__type">{mt.label}</div>
                      <div className="db-fallback-row__desc">{mt.description}</div>
                    </td>
                    <td>
                      <span className={`status-pill ${status.className}`}>{status.label}</span>
                    </td>
                    <td className="db-fallback-row__preview">{preview}</td>
                    <td>
                      <div className="db-fallback-row__actions">
                        <button
                          type="button"
                          className="sf-btn sf-btn--ghost sf-btn--sm text-primary"
                          onClick={(e) => { e.stopPropagation(); populateByAI(mt.key) }}
                          disabled={populatingType === popKey}
                        >
                          {populatingType === popKey ? "Generating..." : "AI"}
                        </button>
                        <button
                          type="button"
                          className="sf-btn sf-btn--ghost sf-btn--sm"
                          onClick={(e) => { e.stopPropagation(); openEditor(mt.key) }}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Section 3: Trivia Pool */}
      <h3 className="hr-section-group mt-8 mb-3">
        Trivia Pool
      </h3>
      <TriviaPoolCard
        pool={triviaPool}
        areaName={nightMode.area_name}
        generatingLang={generatingLang}
        onGenerate={generateTrivia}
      />

      {/* Editor Drawer */}
      {editingType && (
        <FallbackEditorDrawer
          type={editingType}
          lang={activeLang}
          headline={editorHeadline}
          message={editorMessage}
          onHeadlineChange={setEditorHeadline}
          onMessageChange={setEditorMessage}
          onSave={saveEditor}
          onReset={resetFallback}
          onPopulate={() => populateByAI(editingType, true)}
          onClose={closeEditor}
          saving={editorSaving}
          error={editorError}
          populating={populatingType === `${editingType}-${activeLang}`}
          existingSource={getFallbackForType(editingType)?.source}
        />
      )}
    </div>
  )
}

// ─── Night Mode Card ────────────────────────────────────────────────

function NightModeCard({
  config,
  onChange,
  saving,
  success,
}: {
  config: NightModeConfig
  onChange: (updater: (prev: NightModeConfig) => NightModeConfig) => void
  saving: boolean
  success: string
}) {
  const pad = (n: number) => String(n).padStart(2, "0")
  const startTime = `${pad(config.start_hour)}:${pad(config.start_minute)}`
  const endTime = `${pad(config.end_hour)}:${pad(config.end_minute)}`

  const parseTime = (val: string): { hour: number; minute: number } => {
    const [h, m] = val.split(":").map(Number)
    return { hour: isNaN(h) ? 0 : h, minute: isNaN(m) ? 0 : m }
  }

  return (
    <div className="section-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-card__title mb-0">Night Mode</h2>
        <div className="flex items-center gap-2">
          {saving && <span className="hr-save-indicator hr-save-indicator--saving">Saving...</span>}
          {success && <span className="hr-save-indicator hr-save-indicator--success">{success}</span>}
        </div>
      </div>

      <p className="page-header__subtitle mt-0 mb-4 text-xs">
        Shows one short fact about this area during night hours instead of regular messages.
      </p>

      <ToggleSwitch
        id="nm-enabled"
        label="Enable Night Mode"
        checked={config.enabled}
        onChange={(v) => onChange((p) => ({ ...p, enabled: v }))}
      />

      {config.enabled && (
        <div className="mt-4 flex flex-col gap-4">
          {/* Time Range */}
          <div>
            <label className="login-card__label">Time Range</label>
            <div className="db-night-times">
              <input
                type="time"
                className="login-card__input w-full md:w-[130px]"
                value={startTime}
                onChange={(e) => {
                  const t = parseTime(e.target.value)
                  onChange((p) => ({ ...p, start_hour: t.hour, start_minute: t.minute }))
                }}
              />
              <span className="text-sm text-muted-foreground">to</span>
              <input
                type="time"
                className="login-card__input w-full md:w-[130px]"
                value={endTime}
                onChange={(e) => {
                  const t = parseTime(e.target.value)
                  onChange((p) => ({ ...p, end_hour: t.hour, end_minute: t.minute }))
                }}
              />
            </div>
          </div>

          {/* Area Name */}
          <TriviaAreaAutocomplete
            value={config.area_name}
            onChange={(name) => onChange((p) => ({ ...p, area_name: name }))}
          />
        </div>
      )}
    </div>
  )
}

// ─── Language Tabs ──────────────────────────────────────────────────

// ─── Trivia Area Autocomplete ────────────────────────────────────────

function TriviaAreaAutocomplete({
  value,
  onChange,
}: {
  value: string
  onChange: (name: string) => void
}) {
  const [search, setSearch] = useState(value)
  const [predictions, setPredictions] = useState<Array<{ place_id: string; description: string; structured_formatting?: { main_text: string; secondary_text: string } }>>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fallback, setFallback] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => { if (!open) setSearch(value) }, [value, open])

  function handleInput(q: string) {
    setSearch(q)
    onChange(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (q.length < 2 || fallback) { setPredictions([]); setOpen(false); return }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await apiClient.googlePlacesAutocomplete(q, "(regions)")
        if (res.ok && res.predictions) {
          setPredictions(res.predictions)
          setOpen(res.predictions.length > 0)
        } else if (!res.ok && res.error?.includes("not configured")) {
          setFallback(true)
        }
      } catch {
        // Fall back silently
      }
      setLoading(false)
    }, 300)
  }

  async function handleSelect(placeId: string, mainText: string) {
    setOpen(false)
    setSearch(mainText)
    setPredictions([])
    onChange(mainText)
  }

  return (
    <div className="relative">
      <label className="login-card__label" htmlFor="nm-area-name">Trivia Area</label>
      <input
        id="nm-area-name"
        type="text"
        className="login-card__input w-full"
        placeholder="e.g. Falsterbo"
        value={search}
        onChange={(e) => handleInput(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {loading && <div className="sf-spinner sf-spinner--xs sf-autocomplete-spinner" />}
      {open && predictions.length > 0 && (
        <div className="sf-autocomplete-dropdown">
          {predictions.map(p => {
            const mainText = p.structured_formatting?.main_text || p.description.split(",")[0]
            return (
              <div
                key={p.place_id}
                onMouseDown={() => handleSelect(p.place_id, mainText)}
                className="sf-autocomplete-dropdown__item"
              >
                <div className="sf-autocomplete-dropdown__main">{mainText}</div>
                {p.structured_formatting?.secondary_text && (
                  <div className="sf-autocomplete-dropdown__secondary">
                    {p.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      <p className="settings-field-hint mb-0">
        Town or area used for trivia topics.
      </p>
    </div>
  )
}

// ─── Fallback Editor Drawer ─────────────────────────────────────────

function FallbackEditorDrawer({
  type,
  lang,
  headline,
  message,
  onHeadlineChange,
  onMessageChange,
  onSave,
  onReset,
  onPopulate,
  onClose,
  saving,
  error,
  populating,
  existingSource,
}: {
  type: MessageType
  lang: Lang
  headline: string
  message: string
  onHeadlineChange: (v: string) => void
  onMessageChange: (v: string) => void
  onSave: () => void
  onReset: () => void
  onPopulate: () => void
  onClose: () => void
  saving: boolean
  error: string
  populating: boolean
  existingSource?: string
}) {
  const typeLabel = MESSAGE_TYPES.find((m) => m.key === type)?.label || type
  const langLabel = LANGS.find((l) => l.key === lang)?.label || lang

  return (
    <div className="drawer-overlay" onClick={onClose} role="presentation" aria-hidden="true">
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <div>
            <h3 className="drawer__title">{typeLabel} Fallback</h3>
            <span className="text-xs text-muted-foreground">{langLabel}</span>
          </div>
          <button className="drawer__close" type="button" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 18 18" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 5l8 8M13 5l-8 8" /></svg>
          </button>
        </div>

        <div className="drawer__body">
          {existingSource && (
            <div className="mb-4">
              <span className={`status-pill ${existingSource === "ai" ? "status-pill--healthy" : existingSource === "manual" ? "status-pill--warning" : "status-pill--neutral"}`}>
                {existingSource === "ai" ? "AI Draft" : existingSource === "manual" ? "Custom" : "Default"}
              </span>
            </div>
          )}

          <div className="mb-4">
            <label className="login-card__label" htmlFor="fb-headline">Headline</label>
            <input
              id="fb-headline"
              type="text"
              className="login-card__input"
              value={headline}
              onChange={(e) => onHeadlineChange(e.target.value)}
              placeholder="2-4 words"
              maxLength={48}
            />
          </div>

          <div className="mb-4">
            <label className="login-card__label" htmlFor="fb-message">Message</label>
            <textarea
              id="fb-message"
              className="login-card__input h-auto resize-y"
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              placeholder="1-2 short sentences"
              maxLength={300}
              rows={4}
            />
            <div className="text-xs text-muted-foreground mt-1 text-right">
              {message.length}/300
            </div>
          </div>

          <p className="settings-field-hint mt-0 mb-4">
            Used only if automatic updates fail. Keep it timeless — no weather, dates, or venue references.
          </p>

          {error && (
            <div className="sf-result-banner sf-result-banner--error text-xs">
              {error}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              className="db-card__btn db-card__btn--ai py-2 px-4"
              onClick={onPopulate}
              disabled={populating}
            >
              {populating ? "Generating..." : "Populate by AI"}
            </button>
            <button
              type="button"
              className="db-card__btn py-2 px-4"
              onClick={onReset}
              disabled={saving}
            >
              Reset to Default
            </button>
            <div className="flex-1" />
            <button
              type="button"
              className="sf-btn sf-btn--primary sf-btn--sm"
              onClick={onSave}
              disabled={saving || (!headline.trim() && !message.trim())}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Trivia Pool Card ───────────────────────────────────────────────

function TriviaPoolCard({
  pool,
  areaName,
  generatingLang,
  onGenerate,
}: {
  pool: TriviaPoolStatus
  areaName: string
  generatingLang: string | null
  onGenerate: (lang: Lang) => void
}) {
  if (!areaName) {
    return (
      <div className="section-card">
        <p className="page-header__subtitle m-0">
          Set an area name in Night Mode to enable trivia pool generation.
        </p>
      </div>
    )
  }

  const enCount = pool.pools["en"] || 0
  const enFacts = pool.facts.filter(f => f.lang === "en")
  const isGenerating = generatingLang === "en"

  return (
    <div className="section-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-baseline gap-2">
          <h3 className="section-card__title mb-0">{areaName}</h3>
          <span className="text-xs text-muted-foreground">{enCount} fun facts</span>
        </div>
        <button
          type="button"
          className="db-card__btn db-card__btn--ai text-xs"
          onClick={() => onGenerate("en")}
          disabled={isGenerating}
        >
          {isGenerating ? "Generating..." : "Generate"}
        </button>
      </div>

      {enFacts.length > 0 && (
        <div className="flex flex-col gap-2">
          {enFacts.map((f, i) => (
            <div key={i} className="db-trivia-fact">
              <span className="db-trivia-fact__headline">{f.headline}</span>
              <span className="db-trivia-fact__text"> — {f.fact}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── TRMNL Integration Card ──────────────────────────────────────────

function TrmnlIntegrationCard() {
  const { propertyId } = usePropertyContext()
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<{ active: boolean; created_at?: string; last_used_at?: string | null; token?: string | null } | null>(null)
  const [generating, setGenerating] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [error, setError] = useState("")
  const [copied, setCopied] = useState("")

  const loadStatus = useCallback(async () => {
    if (!propertyId) return
    setLoading(true)
    const res = await apiClient.getTrmnlIntegration(propertyId)
    if (res.ok) {
      setStatus({ active: res.active, created_at: res.created_at, last_used_at: res.last_used_at, token: res.token || null })
    }
    setLoading(false)
  }, [propertyId])

  useEffect(() => { loadStatus() }, [loadStatus])

  async function handleGenerate() {
    if (!propertyId) return
    if (status?.active && !window.confirm("Generate a new token? The existing token will be revoked and the device will stop working until reconfigured.")) return
    setGenerating(true)
    setError("")
    const res = await apiClient.generateTrmnlToken(propertyId)
    if (res.ok && res.token) {
      loadStatus()
    } else {
      setError(res.error || "Failed to generate token")
    }
    setGenerating(false)
  }

  async function handleRevoke() {
    if (!propertyId) return
    if (!window.confirm("Revoke the TRMNL token? The device will immediately stop working.")) return
    setRevoking(true)
    setError("")
    const res = await apiClient.revokeTrmnlToken(propertyId)
    if (res.ok) {
      loadStatus()
    } else {
      setError(res.error || "Failed to revoke token")
    }
    setRevoking(false)
  }

  function copyText(text: string, label: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label)
      setTimeout(() => setCopied(""), 2000)
    })
  }

  if (loading) {
    return (
      <div className="section-card mb-6">
        <h2 className="section-card__title">TRMNL Display</h2>
        <div className="text-center p-4">
          <div className="sf-spinner" />
        </div>
      </div>
    )
  }

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
  const activeToken = status?.token || ""
  const pollingUrl = activeToken
    ? `${apiBaseUrl}/api/integrations/trmnl/v1/poll.json?token=${activeToken}`
    : ""
  const svgUrl = activeToken
    ? `${apiBaseUrl}/api/integrations/trmnl/v1/display.svg?token=${activeToken}`
    : ""
  const trmnlMarkup = '<img src="{{ image_url }}" style="width:100%;height:100%;object-fit:cover;" />'

  return (
    <div className="section-card mb-6">
      <h2 className="section-card__title">TRMNL Display</h2>
      <p className="page-header__subtitle mt-0 mb-4 text-xs">
        Connect a TRMNL e-ink device to show live property information — guest messages, weather, and local recommendations.
      </p>

      {error && <div className="sf-result-banner sf-result-banner--error mb-4">{error}</div>}

      {/* Active integration — show status + URLs */}
      {status?.active && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="login-card__label">Status</label>
              <span className="status-pill status-pill--healthy">
                <span className="status-pill__dot" />
                Connected
              </span>
            </div>
            <div>
              <label className="login-card__label">Last Used</label>
              <span className="text-sm">
                {status.last_used_at ? formatRelative(status.last_used_at) : "Never"}
              </span>
            </div>
          </div>

          {pollingUrl && (
            <div className="mb-4">
              <label className="login-card__label">TRMNL Polling URL</label>
              <div className="flex gap-2 items-center">
                <code className="token-display flex-1 text-xs">{pollingUrl}</code>
                <button className="data-table__action h-9 whitespace-nowrap" type="button" onClick={() => copyText(pollingUrl, "polling")}>
                  {copied === "polling" ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="settings-field-hint">
                Paste into TRMNL &rarr; Build your own &rarr; Polling URL. Leave Headers and Body empty.
              </p>
            </div>
          )}

          <div className="mb-4">
            <label className="login-card__label">TRMNL Markup</label>
            <div className="flex gap-2 items-center">
              <code className="token-display flex-1">{trmnlMarkup}</code>
              <button className="data-table__action h-9 whitespace-nowrap" type="button" onClick={() => copyText(trmnlMarkup, "markup")}>
                {copied === "markup" ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="settings-field-hint">Paste into the TRMNL markup editor.</p>
          </div>

          {svgUrl && (
            <div className="mb-4">
              <label className="login-card__label">Direct SVG URL</label>
              <div className="flex gap-2 items-center">
                <code className="token-display flex-1 text-xs">{svgUrl}</code>
                <button className="data-table__action h-9 whitespace-nowrap" type="button" onClick={() => copyText(svgUrl, "svg")}>
                  {copied === "svg" ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="settings-field-hint">Direct link to the SVG display — open in browser to preview.</p>
            </div>
          )}

          <div className="flex gap-3">
            <button className="data-table__action h-[34px]" type="button" onClick={handleGenerate} disabled={generating}>
              {generating ? "Generating..." : "Regenerate Token"}
            </button>
            <button className="data-table__action h-[34px] text-destructive" type="button" onClick={handleRevoke} disabled={revoking}>
              {revoking ? "Revoking..." : "Revoke"}
            </button>
          </div>
        </>
      )}

      {/* No integration — show connect button */}
      {!status?.active && (
        <div className="empty-state p-6">
          <div className="empty-state__title">No device connected</div>
          <div className="empty-state__desc mb-4">
            Generate a token to connect your TRMNL e-ink display.
          </div>
          <button
            className="login-card__submit w-auto px-5 h-9"
            type="button"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? "Generating..." : "Connect TRMNL Device"}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── TV Display Card ─────────────────────────────────────────────────

type TvMode = "standalone" | "complement" | "ambient"

const TV_MODES: { key: TvMode; label: string; desc: string }[] = [
  { key: "standalone", label: "Standalone", desc: "Full info — welcome, WiFi, weather, places, house rules" },
  { key: "complement", label: "Complement", desc: "Visual content only — photos, places, weather (pair with e-ink)" },
  { key: "ambient", label: "Ambient", desc: "Photo screensaver with minimal time/weather overlay" },
]

const DEFAULT_TV_SLIDES: TvSlideConfig[] = [
  { type: "welcome", enabled: true, duration: 15, order: 0 },
  { type: "weather", enabled: true, duration: 15, order: 1 },
  { type: "wifi",    enabled: true, duration: 10, order: 2 },
  { type: "places",  enabled: true, duration: 20, order: 3 },
  { type: "rules",   enabled: true, duration: 15, order: 4 },
  { type: "photo",   enabled: true, duration: 25, order: 5 },
]

const TV_SLIDE_LABELS: Record<string, string> = {
  welcome: "Welcome",
  weather: "Weather",
  wifi: "WiFi & Access",
  places: "Places",
  rules: "House Rules",
  photo: "Cover Photo",
}

function TvDisplayCard() {
  const { propertyId } = usePropertyContext()
  const { toast } = useToast()
  const [slug, setSlug] = useState("")
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState<TvMode>("standalone")
  const [copied, setCopied] = useState(false)
  const [pairPin, setPairPin] = useState("")
  const [pairMode, setPairMode] = useState<TvMode>("standalone")
  const [pairing, setPairing] = useState(false)
  const [pairSuccess, setPairSuccess] = useState(false)

  // Slide layout config state
  const [slideConfig, setSlideConfig] = useState<TvSlideConfig[]>(DEFAULT_TV_SLIDES)
  const [slideConfigDirty, setSlideConfigDirty] = useState(false)
  const [slideConfigSaving, setSlideConfigSaving] = useState(false)
  const slideConfigTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!propertyId) return
    apiClient.getPropertySettings(propertyId).then((res) => {
      const data = res as { ok?: boolean; settings?: { property?: { slug?: string; tv_slide_config?: TvSlideConfig[] | null } } }
      setSlug(String(data.settings?.property?.slug || "").trim().toLowerCase())
      // Load saved slide config or use defaults
      if (Array.isArray(data.settings?.property?.tv_slide_config) && data.settings!.property!.tv_slide_config!.length > 0) {
        setSlideConfig(data.settings!.property!.tv_slide_config!)
      }
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [propertyId])

  // Debounced autosave for slide config (1.5s after last change)
  useEffect(() => {
    if (!slideConfigDirty || !propertyId) return
    if (slideConfigTimerRef.current) clearTimeout(slideConfigTimerRef.current)
    slideConfigTimerRef.current = setTimeout(async () => {
      setSlideConfigSaving(true)
      try {
        await apiClient.savePropertySettings({ tv_slide_config: slideConfig }, propertyId)
        setSlideConfigDirty(false)
      } catch {
        toast("Failed to save slide config.", "error")
      } finally {
        setSlideConfigSaving(false)
      }
    }, 1500)
    return () => {
      if (slideConfigTimerRef.current) clearTimeout(slideConfigTimerRef.current)
    }
  }, [slideConfig, slideConfigDirty, propertyId, toast])

  function updateSlideConfig(updated: TvSlideConfig[]) {
    setSlideConfig(updated)
    setSlideConfigDirty(true)
  }

  function moveSlide(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= slideConfig.length) return
    const updated = [...slideConfig]
    const temp = updated[index]
    updated[index] = updated[target]
    updated[target] = temp
    // Recalculate order values
    updateSlideConfig(updated.map((s, i) => ({ ...s, order: i })))
  }

  function toggleSlide(index: number) {
    const updated = slideConfig.map((s, i) => i === index ? { ...s, enabled: !s.enabled } : s)
    updateSlideConfig(updated)
  }

  function setSlideDuration(index: number, duration: number) {
    const clamped = Math.max(5, Math.min(120, Math.round(duration) || 5))
    const updated = slideConfig.map((s, i) => i === index ? { ...s, duration: clamped } : s)
    updateSlideConfig(updated)
  }

  function resetSlideConfig() {
    updateSlideConfig(DEFAULT_TV_SLIDES.map((s, i) => ({ ...s, order: i })))
  }

  const baseUrl = `${window.location.origin}/p/${slug}/tv`
  const fullUrl = mode === "standalone" ? baseUrl : `${baseUrl}?mode=${mode}`

  function copyUrl() {
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (loading) {
    return (
      <div className="section-card mb-6">
        <h2 className="section-card__title">TV Display</h2>
        <div className="text-center p-4">
          <div className="sf-spinner" />
        </div>
      </div>
    )
  }

  if (!slug) {
    return (
      <div className="section-card mb-6">
        <h2 className="section-card__title">TV Display</h2>
        <p className="page-header__subtitle mt-0 mb-0 text-xs">
          Property slug not configured. Save property settings to enable the TV display.
        </p>
      </div>
    )
  }

  return (
    <div className="section-card mb-6">
      <h2 className="section-card__title">TV Display</h2>
      <p className="page-header__subtitle mt-0 mb-4 text-xs">
        Full-screen display for smart TVs, Fire Stick, or any device with a browser. Auto-rotating slides with weather, places, and property info.
      </p>

      {/* Mode selector */}
      <div className="mb-4">
        <label className="login-card__label">Display Mode</label>
        <div className="flex gap-2 flex-wrap">
          {TV_MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`sf-btn sf-btn--sm ${mode === m.key ? "sf-btn--primary" : "sf-btn--ghost"}`}
              onClick={() => setMode(m.key)}
              title={m.desc}
            >
              {m.label}
            </button>
          ))}
        </div>
        <p className="settings-field-hint mb-0">
          {TV_MODES.find((m) => m.key === mode)?.desc}
        </p>
      </div>

      {/* Slide Layout Editor — standalone mode only */}
      {mode === "standalone" && (
        <div className="mb-4 border-t border-border pt-4">
          <div className="flex justify-between items-center mb-2">
            <label className="login-card__label m-0">Slide Layout</label>
            <div className="flex gap-2 items-center">
              {slideConfigSaving && (
                <span className="text-xs text-muted-foreground">Saving…</span>
              )}
              {slideConfigDirty && !slideConfigSaving && (
                <span className="text-xs text-muted-foreground">Unsaved</span>
              )}
              <button
                type="button"
                className="data-table__action text-xs h-7"
                onClick={resetSlideConfig}
              >
                Reset
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {slideConfig.map((slide, i) => (
              <div
                key={slide.type}
                className={`flex items-center gap-2 py-2 px-3 rounded-md bg-muted transition-opacity duration-150 ${slide.enabled ? "opacity-100" : "opacity-50"}`}
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-px">
                  <button
                    type="button"
                    onClick={() => moveSlide(i, -1)}
                    disabled={i === 0}
                    className={`bg-transparent border-none p-0 leading-none text-xs ${i === 0 ? "cursor-default text-muted-foreground opacity-30" : "cursor-pointer text-foreground opacity-100"}`}
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSlide(i, 1)}
                    disabled={i === slideConfig.length - 1}
                    className={`bg-transparent border-none p-0 leading-none text-xs ${i === slideConfig.length - 1 ? "cursor-default text-muted-foreground opacity-30" : "cursor-pointer text-foreground opacity-100"}`}
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>

                {/* Toggle */}
                <input
                  type="checkbox"
                  checked={slide.enabled}
                  onChange={() => toggleSlide(i)}
                  className="m-0 cursor-pointer"
                />

                {/* Label */}
                <span className="flex-1 text-sm font-medium">
                  {TV_SLIDE_LABELS[slide.type] || slide.type}
                </span>

                {/* Duration */}
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={5}
                    max={120}
                    value={slide.duration}
                    onChange={(e) => setSlideDuration(i, Number(e.target.value))}
                    className="w-[52px] text-right text-xs py-0.5 px-1 border border-border rounded-sm bg-background text-foreground"
                  />
                  <span className="text-xs text-muted-foreground">s</span>
                </div>
              </div>
            ))}
          </div>
          <p className="settings-field-hint mb-0 mt-2">
            Toggle slides on/off, set display time (5–120s), and reorder. Changes auto-save.
          </p>
        </div>
      )}

      {/* URL */}
      <div className="mb-4">
        <label className="login-card__label">TV Display URL</label>
        <div className="flex gap-2 items-center">
          <code className="token-display flex-1 text-xs">{fullUrl}</code>
          <button className="data-table__action h-9 whitespace-nowrap" type="button" onClick={copyUrl}>
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="settings-field-hint">
          Open this URL on your TV browser or kiosk device. Use Fully Kiosk Browser for auto-launch on power-on.
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="data-table__action h-[34px] inline-flex items-center no-underline"
        >
          Preview
        </a>
      </div>

      {/* ── Pair a TV ── */}
      <div className="border-t border-border pt-4 mt-4">
        <h3 className="text-sm font-semibold mb-2 mt-0">
          Pair a TV
        </h3>
        <p className="settings-field-hint mb-3">
          Open the Stayframe app on your Fire TV or Android TV to see a 6-character code, then enter it below.
        </p>

        <div className="flex gap-3 items-end flex-wrap mb-3">
          <div className="flex-[0_0_160px]">
            <label className="login-card__label" htmlFor="tv-pair-pin">TV Code</label>
            <input
              id="tv-pair-pin"
              type="text"
              className="sf-input font-mono tracking-widest text-base"
              placeholder="ABC123"
              maxLength={6}
              value={pairPin}
              onChange={(e) => {
                setPairPin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
                setPairSuccess(false)
              }}
              disabled={pairing}
            />
          </div>
          <div>
            <label className="login-card__label">Mode</label>
            <div className="flex gap-1">
              {TV_MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`sf-btn sf-btn--sm ${pairMode === m.key ? "sf-btn--primary" : "sf-btn--ghost"}`}
                  onClick={() => setPairMode(m.key)}
                  title={m.desc}
                  disabled={pairing}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="sf-btn sf-btn--primary sf-btn--sm h-9"
            onClick={async () => {
              const cleanPin = pairPin.trim().toUpperCase().replace(/[^A-Z0-9]/g, "")
              if (cleanPin.length !== 6) {
                toast("Code must be exactly 6 characters.", "error")
                return
              }
              setPairing(true)
              setPairSuccess(false)
              try {
                const res = await apiClient.resolveTvPairing(cleanPin, propertyId, pairMode)
                if (res.ok) {
                  setPairSuccess(true)
                  setPairPin("")
                  toast("TV paired! It will load your display within a few seconds.", "success")
                } else {
                  const { message } = formatApiError(res as Record<string, unknown>)
                  toast(message, "error")
                }
              } catch {
                toast("Failed to pair TV. Please try again.", "error")
              } finally {
                setPairing(false)
              }
            }}
            disabled={pairing || pairPin.length !== 6}
          >
            {pairing ? "Pairing\u2026" : "Pair"}
          </button>
        </div>

        {pairSuccess && (
          <p className="text-success text-xs m-0">
            TV paired successfully. It will reload within a few seconds.
          </p>
        )}
      </div>
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  if (!iso) return "\u2014"
  try {
    const d = new Date(iso)
    const diffMs = Date.now() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffHr = Math.floor(diffMs / 3600000)
    const diffDay = Math.floor(diffMs / 86400000)
    if (diffMin < 1) return "Just now"
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHr < 24) return `${diffHr}h ago`
    if (diffDay < 7) return `${diffDay}d ago`
    return d.toLocaleDateString()
  } catch {
    return iso
  }
}

// ─── Toggle Switch (reused from HouseRulesPage pattern) ─────────────

function ToggleSwitch({
  id,
  label,
  checked,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="hr-toggle" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        className="hr-toggle__input"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="hr-toggle__track">
        <span className="hr-toggle__thumb" />
      </span>
      <span className="hr-toggle__label">{label}</span>
    </label>
  )
}
